import React, { useMemo, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import GanttHeader from './GanttHeader';
import GanttTaskList from './GanttTaskList';
import GanttGrid from './GanttGrid';
import GanttDatePickerModal from './GanttDatePickerModal';

import { GANTT_VIEW_TYPES, GANTT_GROUP_BY_OPTIONS, normalizeDependencyType } from '../../../shared/appConstants';
import { getLocalDatePart, hasEffectiveDueTime } from '../../../shared/timeUtils';
import { fetchGanttData, setGanttViewType, setGanttCurrentDate, setGanttSelectedDates, setGanttPagination, setGanttGroupByOption, clearError, updateGanttTaskCompletion, updateGanttTicket } from '../../../store/calendarSlice';
import { 
  getDayViewDates, 
  getWeekViewDates, 
  getQuarterViewDates, 
  getYearViewDates,
  calculateDateRangeForView,
  handleBoundaryNavigation,
  sanitizeGanttApiFilters
} from './ganttUtils';
import './GanttView.scss';

const GanttView = ({ onTaskClick, onCreateTask, selectedDate, projectId, filters = {} }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { ganttData, ganttLoading, ganttPagination, ganttViewType, ganttCurrentDate, ganttSelectedDates, ganttGroupByOption, error } = useSelector(state => state.calendar);
  const { tickets: ticketStoreTickets, ticketDetails, lastLinkUpdateAt, lastSubtaskUpdateAt } = useSelector(state => state.tickets);
  
  const [datePickerAnchorEl, setDatePickerAnchorEl] = React.useState(null);
  const [isTaskListCollapsed, setIsTaskListCollapsed] = React.useState(false);
  const [configOptions, setConfigOptions] = React.useState({
    dependencies: false,
    todayIndicator: false
  });
  const groupByOption = ganttGroupByOption || GANTT_GROUP_BY_OPTIONS.TASK_TYPE;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.classList.remove('gantt-fullscreen-mode');
      // Clear any pending timeouts
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      // Abort any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const currentDate = React.useMemo(() => new Date(ganttCurrentDate), [ganttCurrentDate]);
  const lastFetchParamsRef = React.useRef(null);
  const fetchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const suppressNextFetchRef = useRef(false);
  const lastErrorContextRef = useRef(null);
  const lastErrorTimeRef = useRef(null);
  const optimisticUpdatedIdsRef = useRef(new Set());
  const lastTicketUpdateRef = useRef({});

  // Sync ticket updates from other parts of the app to gantt store
  useEffect(() => {
    if (!ticketStoreTickets || !ganttData?.tickets) return;
    
    const buildAssigneeName = (assignee) => {
      if (!assignee) return '';
      if (typeof assignee === 'string') return assignee.trim();
      if (assignee.name) return assignee.name.trim();
      const fallbackName = `${assignee.first_name || ''} ${assignee.last_name || ''}`.trim();
      return fallbackName;
    };

    const normalizeAssignee = (assignee) => {
      if (!assignee) return null;
      if (typeof assignee === 'string') {
        return {
          id: null,
          first_name: assignee,
          last_name: '',
          name: assignee
        };
      }
      if (assignee.first_name || assignee.last_name) return assignee;
      if (assignee.name && typeof assignee.name === 'string') {
        const parts = assignee.name.trim().split(' ');
        const first = parts.shift() || assignee.name;
        const last = parts.join(' ');
        return {
          ...assignee,
          first_name: assignee.first_name ?? first,
          last_name: assignee.last_name ?? last,
          name: assignee.name
        };
      }
      return {
        ...assignee,
        name: buildAssigneeName(assignee)
      };
    };

    const normalizeAssignees = (value) => {
      if (!value) return [];
      if (Array.isArray(value)) {
        return value
          .map(normalizeAssignee)
          .filter(Boolean)
          .map(a => ({ ...a, name: buildAssigneeName(a) }))
          .filter(a => a.name);
      }
      const single = normalizeAssignee(value);
      return single ? [{ ...single, name: buildAssigneeName(single) }].filter(a => a.name) : [];
    };

    const keyify = (value) => {
      if (value && typeof value === 'object') {
        if (value.id !== undefined && value.id !== null) return `id:${value.id}`;
        if (value.name) return `name:${value.name}`;
        return JSON.stringify(value);
      }
      return value ?? null;
    };

    const assigneeKey = (assignee) => {
      if (!assignee) return null;
      if (typeof assignee === 'string') return assignee;
      const fallbackName = `${assignee.first_name || ''} ${assignee.last_name || ''}`.trim();
      return assignee.id ?? (fallbackName || assignee.name || null);
    };

    const assigneesKey = (assignees) => {
      if (!Array.isArray(assignees) || assignees.length === 0) return '';
      return assignees
        .map(a => a?.id ?? a?.name ?? '')
        .filter(Boolean)
        .sort()
        .join('|');
    };

    const extractDependenciesFromLinked = (linkedTickets) => {
      if (!linkedTickets || typeof linkedTickets !== 'object') return [];
      const deps = [];
      const isOutgoing = (value) => {
        const rel = normalizeDependencyType(value);
        return rel === 'blocks' || rel === 'dependent on' || rel === 'depends on' || rel === 'required for';
      };

      Object.entries(linkedTickets).forEach(([relationshipType, relationshipData]) => {
        if (!isOutgoing(relationshipType)) return;
        const tickets = relationshipData?.tickets || [];
        tickets.forEach(ticket => {
          const targetId = ticket?.id ?? ticket?.target_ticket_id ?? ticket?.target_id ?? ticket?.ticket_id;
          if (targetId === undefined || targetId === null) return;
          deps.push({
            targetTaskId: String(targetId),
            relationshipType: String(relationshipType),
            relationship_id: ticket?.relationship_id ?? ticket?.relationshipId ?? ticket?.id,
            targetTicketNumber:
              ticket?.target_ticket_number ??
              ticket?.targetTicketNumber ??
              ticket?.ticket_number ??
              ticket?.ticketNumber ??
              null,
            targetTaskLabel: ticket?.title ?? ticket?.target_title ?? ticket?.targetTaskTitle ?? null
          });
        });
      });
      return deps;
    };

    const dependenciesKey = (dependencies) => {
      if (!Array.isArray(dependencies) || dependencies.length === 0) return '';
      const normalized = dependencies
        .map(dep => {
          const targetId =
            dep.targetTaskId ??
            dep.target_task_id ??
            dep.targetTaskID ??
            dep.targetTask?.id ??
            dep.target_task?.id ??
            dep.target_id ??
            dep.related_ticket_id ??
            dep.target_ticket;
          const relationshipType =
            dep.relationshipType ??
            dep.relationship_type ??
            dep.dependency_type ??
            dep.dependencyType ??
            dep.type ??
            dep.relationship ??
            'blocks';
          const relationshipId = dep.relationship_id ?? dep.relationshipId ?? dep.id ?? '';
          return `${relationshipId}:${String(targetId ?? '')}:${String(relationshipType ?? '')}`;
        })
        .sort();
      return normalized.join('|');
    };

    // Helper function to find and compare tasks recursively
    const findAndCompareTask = (ganttTickets, ticketStoreTickets, taskId) => {
      if (ticketDetails && String(ticketDetails.id) === String(taskId)) {
        return ticketDetails;
      }
      // Search in root level tickets
      for (const statusData of Object.values(ticketStoreTickets)) {
        if (statusData?.tickets) {
          const found = findTaskRecursively(statusData.tickets, taskId);
          if (found) return found;
        }
      }
      return null;
    };
    
    const findTaskRecursively = (tickets, taskId) => {
      for (const ticket of tickets) {
        if (ticket.id === taskId) return ticket;
        if (ticket.subtasks && ticket.subtasks.length > 0) {
          const found = findTaskRecursively(ticket.subtasks, taskId);
          if (found) return found;
        }
      }
      return null;
    };
    
    // Check all tasks (including nested ones) for any changes
    const checkTasksRecursively = (ganttTickets) => {
      ganttTickets.forEach(ganttTicket => {
        // Check this task
        const ticketStoreTask = findAndCompareTask(ganttTickets, ticketStoreTickets, ganttTicket.id);
        if (ticketStoreTask) {
          const lastUpdate = lastTicketUpdateRef.current[ganttTicket.id] || {};
          
          // Check for any field changes
          const updates = {};
          let hasChanges = false;

          const completedValue = ticketStoreTask.marked_as_completed ?? ticketStoreTask.completed ?? ticketStoreTask.is_completed;
          const normalizedAssignees = normalizeAssignees(
            ticketStoreTask.assignees ??
            ticketStoreTask.users ??
            ticketStoreTask.assignee ??
            ticketStoreTask.user
          );
          const normalizedAssignee = normalizedAssignees[0] || null;
          const hasStartDate =
            Object.prototype.hasOwnProperty.call(ticketStoreTask, 'start_date') ||
            Object.prototype.hasOwnProperty.call(ticketStoreTask, 'startDate');
          const hasDueDate =
            Object.prototype.hasOwnProperty.call(ticketStoreTask, 'due_date') ||
            Object.prototype.hasOwnProperty.call(ticketStoreTask, 'dueDate') ||
            Object.prototype.hasOwnProperty.call(ticketStoreTask, 'end_date') ||
            Object.prototype.hasOwnProperty.call(ticketStoreTask, 'endDate');

          const normalizedStartDate = hasStartDate
            ? (ticketStoreTask.start_date ?? ticketStoreTask.startDate)
            : undefined;
          const normalizedDueDate = hasDueDate
            ? (ticketStoreTask.due_date ?? ticketStoreTask.dueDate ?? ticketStoreTask.end_date ?? ticketStoreTask.endDate)
            : undefined;
          const hasTaskType =
            Object.prototype.hasOwnProperty.call(ticketStoreTask, 'task_type') ||
            Object.prototype.hasOwnProperty.call(ticketStoreTask, 'taskType');
          const hasPriority =
            Object.prototype.hasOwnProperty.call(ticketStoreTask, 'priority');
          const hasAssignee =
            Object.prototype.hasOwnProperty.call(ticketStoreTask, 'assignees') ||
            Object.prototype.hasOwnProperty.call(ticketStoreTask, 'assignee') ||
            Object.prototype.hasOwnProperty.call(ticketStoreTask, 'users') ||
            Object.prototype.hasOwnProperty.call(ticketStoreTask, 'user');
          const hasTitle =
            Object.prototype.hasOwnProperty.call(ticketStoreTask, 'title');

          const normalizedTaskType = hasTaskType
            ? (ticketStoreTask.task_type ?? ticketStoreTask.taskType)
            : undefined;
          const normalizedPriority = hasPriority ? ticketStoreTask.priority : undefined;
          const hasStatus =
            Object.prototype.hasOwnProperty.call(ticketStoreTask, 'status') ||
            Object.prototype.hasOwnProperty.call(ticketStoreTask, 'status_name') ||
            Object.prototype.hasOwnProperty.call(ticketStoreTask, 'statusName') ||
            Object.prototype.hasOwnProperty.call(ticketStoreTask, 'status_id') ||
            Object.prototype.hasOwnProperty.call(ticketStoreTask, 'statusId');
          const normalizedStatus = hasStatus ? ticketStoreTask.status : undefined;
          const normalizedDependencies =
            (ticketStoreTask.dependencies && ticketStoreTask.dependencies.length > 0)
              ? ticketStoreTask.dependencies
              : extractDependenciesFromLinked(ticketStoreTask.linked_tickets);

          // Check completion status
          if (lastUpdate.marked_as_completed !== completedValue) {
            updates.marked_as_completed = completedValue;
            updates.completed = completedValue;
            updates.is_completed = completedValue;
            hasChanges = true;
          }
          
          // Check other common fields
          if (hasTitle && lastUpdate.title !== ticketStoreTask.title) {
            updates.title = ticketStoreTask.title;
            hasChanges = true;
          }
          
          if (hasStartDate && lastUpdate.start_date !== normalizedStartDate) {
            updates.start_date = normalizedStartDate;
            hasChanges = true;
          }
          
          if (hasDueDate && lastUpdate.due_date !== normalizedDueDate) {
            updates.due_date = normalizedDueDate;
            hasChanges = true;
          }
          
          if (hasTaskType && lastUpdate.task_type !== normalizedTaskType) {
            updates.task_type = normalizedTaskType;
            hasChanges = true;
          }
          
          if (hasPriority && lastUpdate.priorityKey !== keyify(normalizedPriority)) {
            updates.priority = normalizedPriority;
            hasChanges = true;
          }
          
          if (hasStatus && lastUpdate.statusKey !== keyify(normalizedStatus)) {
            updates.status = normalizedStatus;
            hasChanges = true;
          }
          
          if (hasAssignee && lastUpdate.assigneesKey !== assigneesKey(normalizedAssignees)) {
            updates.assignees = normalizedAssignees;
            updates.assignee = normalizedAssignee;
            hasChanges = true;
          }

          if (lastUpdate.dependenciesKey !== dependenciesKey(normalizedDependencies)) {
            updates.dependencies = normalizedDependencies;
            hasChanges = true;
          }

          if (hasChanges) {
            // Use appropriate update action based on what changed
            const { marked_as_completed, completed, is_completed, ...nonCompletionUpdates } = updates;
            if (marked_as_completed !== undefined) {
              dispatch(updateGanttTaskCompletion({ taskId: ganttTicket.id, completed: marked_as_completed }));
            }
            if (Object.keys(nonCompletionUpdates).length > 0) {
              dispatch(updateGanttTicket({ ticketId: ganttTicket.id, updates: nonCompletionUpdates }));
            }
            
            // Update our tracking
            const nextTicketUpdate = {
              ...lastUpdate,
              marked_as_completed: completedValue,
              title: hasTitle ? ticketStoreTask.title : lastUpdate.title,
              task_type: hasTaskType ? normalizedTaskType : lastUpdate.task_type,
              priorityKey: hasPriority ? keyify(normalizedPriority) : lastUpdate.priorityKey,
              statusKey: hasStatus ? keyify(normalizedStatus) : lastUpdate.statusKey,
              assigneeKey: hasAssignee ? assigneeKey(normalizedAssignee) : lastUpdate.assigneeKey,
              assigneesKey: hasAssignee ? assigneesKey(normalizedAssignees) : lastUpdate.assigneesKey,
              dependenciesKey: dependenciesKey(normalizedDependencies)
            };
            if (hasStartDate) {
              nextTicketUpdate.start_date = normalizedStartDate;
            }
            if (hasDueDate) {
              nextTicketUpdate.due_date = normalizedDueDate;
            }
            lastTicketUpdateRef.current[ganttTicket.id] = nextTicketUpdate;
          }
        }
        
        // Check subtasks recursively
        if (ganttTicket.subtasks && ganttTicket.subtasks.length > 0) {
          checkTasksRecursively(ganttTicket.subtasks);
        }
      });
    };
    
    checkTasksRecursively(ganttData.tickets);
  }, [ticketStoreTickets, ticketDetails, ganttData?.tickets, dispatch]);

  // Define formatDate function early to avoid initialization issues
  const formatDate = useCallback((date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const { epic, epicStartDate, epicDueDate, startDate, dueDate } = filters;

  // Fetch calendar data when date range or filters change
  const filtersString = useMemo(() => {
    // Create a stable string representation of filters
    const stableFilters = { ...filters };
    // Remove functions and unstable references
    Object.keys(stableFilters).forEach(key => {
      if (typeof stableFilters[key] === 'function') {
        delete stableFilters[key];
      }
    });
    return JSON.stringify(stableFilters);
  }, [filters]);

  useEffect(() => {
    optimisticUpdatedIdsRef.current.clear();
  }, [filtersString, ganttViewType, projectId]);
  
  // Replace the existing calculateDateRange logic with the utility function
  const dateRange = useMemo(() => {
    // Priority 1: Epic filter dates
    if (epic && epic !== 'all' && epicStartDate) {
      return {
        fromDate: epicStartDate.split('T')[0],
        toDate: epicDueDate ? epicDueDate.split('T')[0] : epicStartDate.split('T')[0]
      };
    }
    
    // Priority 2: Start/Due date filters - prioritize startDate if both exist
    if (startDate || dueDate) {
      // Use startDate if available, otherwise use dueDate
      const filterDate = new Date(startDate || dueDate);
      return calculateDateRangeForView(filterDate, ganttViewType);
    }
    
    // Priority 3: View-based ranges using current date
    if (ganttViewType === 'Day' && ganttSelectedDates?.length > 0) {
      return { fromDate: ganttSelectedDates[0], toDate: ganttSelectedDates[ganttSelectedDates.length - 1] };
    }
    
    return calculateDateRangeForView(new Date(ganttCurrentDate), ganttViewType);
  }, [ganttCurrentDate, ganttViewType, epic, epicStartDate, epicDueDate, startDate, dueDate, ganttSelectedDates]);

  const queryKey = useMemo(() => {
    if (!projectId || !dateRange.fromDate || !dateRange.toDate) return null;
    return `${projectId}-${dateRange.fromDate}-${dateRange.toDate}-${ganttViewType}-${filtersString}-${ganttPagination.currentPage}-${lastLinkUpdateAt || 0}-${lastSubtaskUpdateAt || 0}`;
  }, [projectId, dateRange.fromDate, dateRange.toDate, ganttViewType, filtersString, ganttPagination.currentPage, lastLinkUpdateAt, lastSubtaskUpdateAt]);


  const buildApiFilters = useCallback(() => {
    return sanitizeGanttApiFilters(filters);
  }, [filters]);
  
  // Handle API errors with toast messages - prevent duplicate toasts for same request
  useEffect(() => {
    if (error) {
      // Create context for this error (includes request parameters)
      const errorContext = `${error}-${projectId}-${dateRange?.fromDate}-${dateRange?.toDate}-${ganttViewType}-${filtersString}-${ganttPagination.currentPage}`;
      const currentTime = Date.now();
      
      // Show toast if context is different OR if same context but more than 2 seconds apart (different user action)
      const timeSinceLastError = currentTime - (lastErrorTimeRef.current || 0);
      const shouldShowToast = errorContext !== lastErrorContextRef.current || timeSinceLastError > 2000;
      
      if (shouldShowToast) {
        toast.error(error);
        lastErrorContextRef.current = errorContext;
        lastErrorTimeRef.current = currentTime;
        dispatch(clearError());
      } else {
        dispatch(clearError()); // Still clear the error even if not showing toast
      }
    }
  }, [error, dispatch, projectId, dateRange?.fromDate, dateRange?.toDate, ganttViewType, filtersString, ganttPagination.currentPage]);
  
  // Reset pagination when view type, filters, or date range changes - with debounce
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (ganttPagination.currentPage !== 1) {
        dispatch(setGanttPagination({ ...ganttPagination, currentPage: 1 }));
      }
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [ganttViewType, filtersString, dateRange.fromDate, dateRange.toDate, dispatch]);
  /* eslint-enable react-hooks/exhaustive-deps */
  
  // Main fetch effect - driven by a single queryKey
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!queryKey) return;
    if (suppressNextFetchRef.current) {
      suppressNextFetchRef.current = false;
      return;
    }

    if (lastFetchParamsRef.current === queryKey) return;
    lastFetchParamsRef.current = queryKey;

    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = null;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    fetchTimeoutRef.current = setTimeout(() => {
      abortControllerRef.current = new AbortController();
      const apiFilters = buildApiFilters();

      if (epic && epic !== "all" && epicStartDate) {
        const epicDate = new Date(epicStartDate);
        if (epicDate.toISOString() !== ganttCurrentDate) {
          dispatch(setGanttCurrentDate(epicDate.toISOString()));
        }
      } else if (startDate || dueDate) {
        const filterDate = new Date(startDate || dueDate);
        if (filterDate.toISOString() !== ganttCurrentDate) {
          dispatch(setGanttCurrentDate(filterDate.toISOString()));
        }
      }

      dispatch(fetchGanttData({
        projectId,
        fromDate: dateRange.fromDate,
        toDate: dateRange.toDate,
        filters: apiFilters,
        page: ganttPagination.currentPage
      }));
    }, 300);

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
    };
  }, [queryKey]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Transform API data to match component format, but merge with Redux store for optimistic updates
  const tasks = useMemo(() => {
    const normalizeDateString = (raw) => {
      if (!raw) return null;
      const dateStr = String(raw).includes('T') ? getLocalDatePart(raw) : raw;
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/').map(Number);
        if (parts.length === 3) {
          const [p1, p2, p3] = parts;
          // Handle MM/DD/YYYY or YYYY/MM/DD
          const year = String(p1).length === 4 ? p1 : p3;
          const month = String(p1).length === 4 ? p2 : p1;
          const day = String(p1).length === 4 ? p3 : p2;
          if (year && month && day) {
            return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          }
        }
      }
      if (dateStr.includes('-')) {
        const parts = dateStr.split('-');
        if (parts.length === 3) {
          const [year, month, day] = parts;
          if (year && month && day) {
            return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          }
        }
      }
      return dateStr;
    };

    const normalizeDependency = (dep) => {
      if (!dep) return null;
      const targetId =
        dep.targetTaskId ??
        dep.target_task_id ??
        dep.targetTaskID ??
        dep.targetTask?.id ??
        dep.target_task?.id ??
        dep.target_id;

      if (targetId === undefined || targetId === null) return null;

      const relationshipType =
        dep.relationshipType ??
        dep.relationship_type ??
        dep.dependency_type ??
        dep.dependencyType ??
        dep.type ??
        dep.relationship ??
        'blocks';

      const targetTaskLabel =
        dep.targetTaskTitle ??
        dep.target_task_title ??
        dep.targetTaskName ??
        dep.target_task_name ??
        dep.targetTitle ??
        dep.target_title ??
        dep.target?.title ??
        dep.target_task?.title ??
        null;

      const targetTicketNumber =
        dep.targetTicketNumber ??
        dep.target_ticket_number ??
        dep.ticket_number ??
        dep.target?.ticket_number ??
        dep.target_task?.ticket_number ??
        null;

      return {
        targetTaskId: String(targetId),
        relationshipType: String(relationshipType),
        colour: dep.colour ?? dep.color ?? dep.colourCode ?? dep.colour_code ?? dep.hex,
        targetTaskLabel,
        targetTicketNumber
      };
    };

    const normalizeAssigneeForTask = (assignee) => {
      if (!assignee) return null;
      if (typeof assignee === 'string') {
        return { id: null, name: assignee, first_name: assignee, last_name: '' };
      }
      const name = assignee.name || `${assignee.first_name || ''} ${assignee.last_name || ''}`.trim();
      return {
        ...assignee,
        name
      };
    };

    const normalizeAssigneesForTask = (value) => {
      if (!value) return [];
      if (Array.isArray(value)) {
        return value
          .map(normalizeAssigneeForTask)
          .filter(Boolean)
          .filter(a => a.name);
      }
      const single = normalizeAssigneeForTask(value);
      return single ? [single].filter(a => a.name) : [];
    };

    const transformTask = (ticket) => {
      const assignees = normalizeAssigneesForTask(
        ticket.assignees ??
        ticket.users ??
        ticket.assignee ??
        ticket.user
      );
      const primaryAssignee = assignees[0] || null;
      return ({
      id: String(ticket.id),
      title: ticket.title,
      startDate: normalizeDateString(ticket.start_date),
      dueDate: normalizeDateString(ticket.due_date),
      dueDateRaw: ticket.due_date,
      hasDueTime: hasEffectiveDueTime(ticket.has_due_time, ticket.due_date),
      priority: ticket.priority,
      status: ticket.status?.name ?? ticket.status?.label ?? ticket.status_name ?? ticket.statusName ?? ticket.status,
      statusId: ticket.status?.id ?? ticket.status_id ?? ticket.statusId ?? null,
      priorityId: ticket.priority?.id ?? ticket.priority_id ?? ticket.priorityId ?? null,
      taskType: ticket.task_type,
      taskTypeId: ticket.task_type?.id ?? ticket.task_type_id ?? ticket.taskTypeId ?? null,
      assignees,
      assignee: primaryAssignee,
      ticketNumber: ticket.ticket_number,
      // Map completion field
      completed: ticket.marked_as_completed ?? false,
      marked_as_completed: ticket.marked_as_completed ?? false,
      is_completed: ticket.marked_as_completed ?? false,
      subtasks: ticket.subtasks?.map(transformTask) || [],
      dependencies: (ticket.dependencies || []).map(normalizeDependency).filter(Boolean)
    });
    };

    const tasksTree = ganttData.tickets?.map(transformTask) || [];

    const taskMap = new Map();
    const collectTasks = (list) => {
      list.forEach(item => {
        taskMap.set(String(item.id), item);
        if (item.subtasks?.length) collectTasks(item.subtasks);
      });
    };
    collectTasks(tasksTree);

    const normalizeRel = (value) => normalizeDependencyType(value);

    const adjustDependencies = (list) => {
      list.forEach(item => {
        if (item.dependencies && item.dependencies.length > 0) {
          const nextDeps = [];
          item.dependencies.forEach(dep => {
            const rel = normalizeRel(dep.relationshipType);
            if (rel === 'dependent on' || rel === 'depends on') {
              const targetTask = taskMap.get(String(dep.targetTaskId));
              if (targetTask) {
                const derived = {
                  targetTaskId: String(item.id),
                  relationshipType: 'Required For',
                  colour: dep.colour,
                  targetTaskLabel: item.title,
                  targetTicketNumber: item.ticketNumber
                };
                const targetDeps = targetTask.dependencies || [];
                const exists = targetDeps.some(existing =>
                  String(existing.targetTaskId) === String(derived.targetTaskId) &&
                  normalizeRel(existing.relationshipType) === 'required for'
                );
                if (!exists) {
                  targetTask.dependencies = [...targetDeps, derived];
                }
              }
              return;
            }
            if (rel === 'blocks' || rel === 'required for') {
              const exists = nextDeps.some(existing =>
                String(existing.targetTaskId) === String(dep.targetTaskId) &&
                normalizeRel(existing.relationshipType) === normalizeRel(dep.relationshipType)
              );
              if (!exists) {
                nextDeps.push(dep);
              }
            }
          });
          item.dependencies = nextDeps;
        }
        if (item.subtasks?.length) adjustDependencies(item.subtasks);
      });
    };
    adjustDependencies(tasksTree);

    const hasActiveFilters =
      (filters.assignee && filters.assignee !== 'all') ||
      (filters.status && filters.status !== 'all') ||
      (filters.priority && filters.priority !== 'all') ||
      (filters.taskType && filters.taskType !== 'all');

    if (!hasActiveFilters) {
      return tasksTree;
    }

    const buildFilterCandidates = (value) => {
      if (value === null || value === undefined) return [];
      const items = [];
      const pushVal = (v) => {
        if (v === null || v === undefined) return;
        const str = String(v).trim();
        if (str) items.push(str.toLowerCase());
      };
      if (Array.isArray(value)) {
        value.forEach(v => pushVal(v));
      } else if (typeof value === 'object') {
        pushVal(value.id);
        pushVal(value.value);
        pushVal(value.name);
        pushVal(value.label);
        pushVal(value.title);
      } else {
        pushVal(value);
      }
      return Array.from(new Set(items));
    };

    const candidatesByFilter = {
      assignee: buildFilterCandidates(filters.assignee),
      status: buildFilterCandidates(filters.status),
      priority: buildFilterCandidates(filters.priority),
      taskType: buildFilterCandidates(filters.taskType)
    };

    const matchesAssignee = (task) => {
      if (!filters.assignee || filters.assignee === 'all') return true;
      const candidates = candidatesByFilter.assignee;
      if (candidates.length === 0) return true;
      return (task.assignees || []).some(a => {
        const idStr = a?.id !== null && a?.id !== undefined ? String(a.id).toLowerCase() : '';
        const nameStr = a?.name ? String(a.name).toLowerCase() : '';
        return (idStr && candidates.includes(idStr)) || (nameStr && candidates.includes(nameStr));
      });
    };

    const matchesStatus = (task) => {
      if (!filters.status || filters.status === 'all') return true;
      const candidates = candidatesByFilter.status;
      if (candidates.length === 0) return true;
      const statusIdStr = task.statusId !== null && task.statusId !== undefined ? String(task.statusId).toLowerCase() : '';
      const statusName = task.status ? String(task.status).toLowerCase() : '';
      if (!statusIdStr && !statusName) return true;
      return (statusIdStr && candidates.includes(statusIdStr)) || (statusName && candidates.includes(statusName));
    };

    const matchesPriority = (task) => {
      if (!filters.priority || filters.priority === 'all') return true;
      const candidates = candidatesByFilter.priority;
      if (candidates.length === 0) return true;
      const priorityIdStr = task.priorityId !== null && task.priorityId !== undefined ? String(task.priorityId).toLowerCase() : '';
      const priorityName = task.priority?.name ?? task.priority;
      const priorityNameStr = priorityName ? String(priorityName).toLowerCase() : '';
      if (!priorityIdStr && !priorityNameStr) return true;
      return (priorityIdStr && candidates.includes(priorityIdStr)) || (priorityNameStr && candidates.includes(priorityNameStr));
    };

    const matchesTaskType = (task) => {
      if (!filters.taskType || filters.taskType === 'all') return true;
      const candidates = candidatesByFilter.taskType;
      if (candidates.length === 0) return true;
      const typeIdStr = task.taskTypeId !== null && task.taskTypeId !== undefined ? String(task.taskTypeId).toLowerCase() : '';
      const typeName = task.taskType?.name ?? task.taskType;
      const typeNameStr = typeName ? String(typeName).toLowerCase() : '';
      if (!typeIdStr && !typeNameStr) return true;
      return (typeIdStr && candidates.includes(typeIdStr)) || (typeNameStr && candidates.includes(typeNameStr));
    };

    const matchesFilters = (task) =>
      matchesAssignee(task) &&
      matchesStatus(task) &&
      matchesPriority(task) &&
      matchesTaskType(task);

    const filterOptimisticOnly = (list) => {
      return list
        .map((task) => {
          const nextSubtasks = task.subtasks?.length ? filterOptimisticOnly(task.subtasks) : [];
          const isOptimistic = optimisticUpdatedIdsRef.current.has(String(task.id));
          const selfMatches = !isOptimistic || matchesFilters(task);
          if (!selfMatches && nextSubtasks.length === 0) return null;
          return { ...task, subtasks: nextSubtasks };
        })
        .filter(Boolean);
    };

    return filterOptimisticOnly(tasksTree);
  }, [ganttData, filters.assignee, filters.status, filters.priority, filters.taskType]);

  const handleDateChange = (newDate, selectedDates) => {
    dispatch(setGanttCurrentDate(newDate.toISOString()));
    if (selectedDates) {
      dispatch(setGanttSelectedDates(selectedDates));
    }
    setDatePickerAnchorEl(null);
  };

  // Keep Day view selected range aligned with the current date
  useEffect(() => {
    if (ganttViewType !== GANTT_VIEW_TYPES.DAY) return;
    const baseDate = new Date(ganttCurrentDate);
    if (isNaN(baseDate.getTime())) return;
    const rangeLength = Math.max(1, ganttSelectedDates?.length || 2);
    const nextDates = [];
    for (let i = 0; i < rangeLength; i++) {
      const next = new Date(baseDate);
      next.setDate(baseDate.getDate() + i);
      nextDates.push(formatDate(next));
    }
    const currentKey = (ganttSelectedDates || []).join('|');
    const nextKey = nextDates.join('|');
    if (currentKey !== nextKey) {
      dispatch(setGanttSelectedDates(nextDates));
    }
  }, [ganttViewType, ganttCurrentDate, ganttSelectedDates, dispatch, formatDate]);


  const handleYearSelect = (year) => {
    const newDate = new Date(year, 0, 1); // January 1st of selected year
    handleDateChange(newDate);
  };

  const handleNavigate = (direction) => {
    const newDate = new Date(currentDate);
    
    if (ganttViewType === GANTT_VIEW_TYPES.DAY) {
      // Navigate by the number of selected days
      const daysToMove = ganttSelectedDates.length || 1;
      newDate.setDate(newDate.getDate() + (direction * daysToMove));
      
      // Update selected dates to maintain the same span
      const newSelectedDates = [];
      for (let i = 0; i < daysToMove; i++) {
        const date = new Date(newDate);
        date.setDate(newDate.getDate() + i);
        newSelectedDates.push(formatDate(date));
      }
      dispatch(setGanttSelectedDates(newSelectedDates));
    } else if (ganttViewType === GANTT_VIEW_TYPES.WEEK) {
      newDate.setMonth(newDate.getMonth() + direction);
    } else if (ganttViewType === GANTT_VIEW_TYPES.QUARTER) {
      newDate.setMonth(newDate.getMonth() + (direction * 3));
    } else if (ganttViewType === GANTT_VIEW_TYPES.YEAR) {
      newDate.setFullYear(newDate.getFullYear() + direction);
    }
    
    dispatch(setGanttCurrentDate(newDate.toISOString()));
  };

  const handleTaskClick = useCallback((task) => {
    if (onTaskClick) {
      // Create an optimistic update handler to pass to the modal
      const optimisticUpdateHandler = (taskId, updates) => {
        const affectsFilters =
          Object.prototype.hasOwnProperty.call(updates, 'assignee') ||
          Object.prototype.hasOwnProperty.call(updates, 'assignees') ||
          Object.prototype.hasOwnProperty.call(updates, 'status') ||
          Object.prototype.hasOwnProperty.call(updates, 'priority') ||
          Object.prototype.hasOwnProperty.call(updates, 'task_type') ||
          Object.prototype.hasOwnProperty.call(updates, 'taskType');
        if (affectsFilters) {
          optimisticUpdatedIdsRef.current.add(String(taskId));
        }
        // Handle completion updates - only update calendar slice for Gantt view
        if ('marked_as_completed' in updates) {
          dispatch(updateGanttTaskCompletion({ taskId, completed: updates.marked_as_completed }));
        } else {
          // Handle other updates
          dispatch(updateGanttTicket({ ticketId: taskId, updates }));
        }
      };
      
      // Pass both task and optimistic update handler
      onTaskClick(task, optimisticUpdateHandler);
    }
  }, [onTaskClick, dispatch]);

  const handleBoundaryTaskClick = useCallback((boundaryInfo, direction, ticketId = null) => {
    suppressNextFetchRef.current = true;
    handleBoundaryNavigation(
      boundaryInfo, 
      direction, 
      ganttViewType, 
      dispatch, 
      setGanttCurrentDate, 
      setGanttSelectedDates, 
      fetchGanttData, 
      projectId, 
      filters,
      ticketId
    );
  }, [ganttViewType, dispatch, projectId, filters]);

  const handlePageChange = (newPage) => {
    dispatch(setGanttPagination({
      ...ganttPagination,
      currentPage: newPage
    }));
  };

  const handleFirstPage = () => handlePageChange(1);
  const handlePrevPage = () => handlePageChange(Math.max(1, ganttPagination.currentPage - 1));
  const handleNextPage = () => handlePageChange(Math.min(ganttPagination.totalPages, ganttPagination.currentPage + 1));
  const handleLastPage = () => handlePageChange(ganttPagination.totalPages);

  const { dates, headers, displayText } = useMemo(() => {
    let result;
    if (ganttViewType === 'Day') {
      result = getDayViewDates(ganttSelectedDates);
    } else if (ganttViewType === 'Week') {
      result = getWeekViewDates(currentDate);
    } else if (ganttViewType === 'Quarter') {
      result = getQuarterViewDates(currentDate);
    } else if (ganttViewType === 'Year') {
      result = getYearViewDates(currentDate);
    } else {
      result = { dates: [], headers: [], displayText: '' };
    }
    
    return result;
  }, [ganttViewType, currentDate, ganttSelectedDates]);

  const gridDateRange = useMemo(() => {
    if (dates.length === 0) return { fromDate: null, toDate: null };
    return {
      fromDate: dates[0],
      toDate: dates[dates.length - 1]
    };
  }, [dates]);

  return (
    <Box className={`gantt-view${isTaskListCollapsed ? " task-list-collapsed" : ""}`}>
      <GanttHeader
        displayText={displayText}
        viewType={ganttViewType}
        onViewTypeChange={(type) => dispatch(setGanttViewType(type))}
        onDatePickerOpen={(anchorEl) => setDatePickerAnchorEl(anchorEl)}
        onNavigate={handleNavigate}
        configOptions={configOptions}
        onConfigChange={setConfigOptions}
        groupByOption={groupByOption}
        onGroupByChange={(value) => dispatch(setGanttGroupByOption(value))}
        tasks={tasks}
        dateRange={gridDateRange}
        onYearSelect={handleYearSelect}
      />
      
      {ganttLoading ? (
        <Box className="gantt-loader">
          <CircularProgress />
          <Typography className="gantt-loader-text">{t('gantt.loadingGanttData')}</Typography>
        </Box>
      ) : (
        <>
          <Box className="gantt-main-content">
            <GanttTaskList 
              tasks={tasks} 
              onTaskClick={handleTaskClick}
              isCollapsed={isTaskListCollapsed}
              onToggleCollapse={() => setIsTaskListCollapsed(!isTaskListCollapsed)}
              pagination={ganttPagination}
              onFirstPage={handleFirstPage}
              onPrevPage={handlePrevPage}
              onNextPage={handleNextPage}
              onLastPage={handleLastPage}
              groupByOption={groupByOption}
              filters={filters}
            />
            <GanttGrid
              dates={dates}
              headers={headers}
              tasks={tasks}
              viewType={ganttViewType}
              onTaskClick={handleTaskClick}
              onBoundaryClick={handleBoundaryTaskClick}
              configOptions={configOptions}
              groupByOption={groupByOption}
              projectId={projectId}
              filters={filters}
            />
          </Box>
          <Box className="gantt-legend">
            <Box className="gantt-legend-item">
              <span className="gantt-legend-swatch gantt-legend-incomplete" />
              <Typography className="gantt-legend-label" variant="body2">
                {t('gantt.incomplete')}
              </Typography>
            </Box>
            <Box className="gantt-legend-item">
              <span className="gantt-legend-swatch gantt-legend-completed" />
              <Typography className="gantt-legend-label" variant="body2">
                {t('gantt.completed')}
              </Typography>
            </Box>
          </Box>
        </>
      )}

      {ganttViewType !== GANTT_VIEW_TYPES.YEAR && (
        <GanttDatePickerModal
          open={Boolean(datePickerAnchorEl)}
          anchorEl={datePickerAnchorEl}
          currentDate={currentDate}
          viewType={ganttViewType}
          selectedDates={ganttSelectedDates}
          onClose={() => setDatePickerAnchorEl(null)}
          onDateChange={handleDateChange}
        />
      )}
    </Box>
  );
};

export default GanttView;
